import {
  ActionIcon,
  Alert,
  Badge,
  CopyButton,
  Divider,
  Group,
  Modal,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import type { ReactNode } from 'react';

import { usePlannedOperation } from '../api/queries';
import type { PlannedOperationStatus } from '../api/types';
import { formatDate, formatFullDateTime, formatKopecks, formatMoney } from '../lib/format';
import { KIND_COLOR } from '../theme';
import { CategoryBadge } from './CategoryBadge';
import { KindBadge } from './KindBadge';

interface PlannedOperationDetailsProps {
  plannedOperationId: string | null;
  onClose: () => void;
}

const STATUS: Record<PlannedOperationStatus, { label: string; color: string }> = {
  tentative: { label: 'предварительный', color: 'gray' },
  confirmed: { label: 'подтверждён', color: 'brand' },
  completed: { label: 'выполнен', color: 'teal' },
  cancelled: { label: 'отменён', color: 'red' },
};

/** Details are re-fetched by id because plans can be edited and completed through MCP. */
export function PlannedOperationDetails({
  plannedOperationId,
  onClose,
}: PlannedOperationDetailsProps) {
  const { data, isPending, error } = usePlannedOperation(plannedOperationId);

  return (
    <Modal
      opened={plannedOperationId !== null}
      onClose={onClose}
      size="md"
      padding="lg"
      centered
      title={<Text fw={600}>Разовый план</Text>}
    >
      {error ? (
        <Alert color="red" variant="light" title="План недоступен">
          {error.message}
        </Alert>
      ) : isPending || !data ? (
        <Stack gap="md">
          <Skeleton height={72} radius="sm" />
          <Skeleton height={20} radius="sm" />
          <Skeleton height={96} radius="sm" />
        </Stack>
      ) : (
        <Stack gap="lg">
          <Stack gap={4}>
            <Text fz="lg" fw={600} style={{ overflowWrap: 'anywhere' }}>
              {data.title}
            </Text>
            <Text
              fz={32}
              fw={600}
              c={KIND_COLOR[data.kind]}
              style={{ fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}
            >
              {formatMoney(data.signed_amount, { withSign: true })}
            </Text>
            <Group gap="xs">
              <KindBadge kind={data.kind} />
              <StatusBadge status={data.status} />
              <Text size="xs" c="dimmed">
                {formatKopecks(data.amount)} · {data.currency}
              </Text>
            </Group>
          </Stack>

          <Divider />

          <Stack gap="md">
            {data.description && (
              <Field label="Описание">
                <Text size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {data.description}
                </Text>
              </Field>
            )}

            <Field label="Категория">
              <CategoryBadge category={data.category} />
            </Field>

            <Field label="Запланировано на">{formatDate(data.due_on)}</Field>
            <Field label="Создан">{formatFullDateTime(data.created_at)}</Field>
            <Field label="Обновлён">{formatFullDateTime(data.updated_at)}</Field>

            {data.completed_at && (
              <Field label="Выполнен">{formatFullDateTime(data.completed_at)}</Field>
            )}

            {data.actual_operation_id && (
              <IdField label="Фактическая операция" value={data.actual_operation_id} />
            )}

            <IdField label="Идентификатор плана" value={data.id} />
          </Stack>
        </Stack>
      )}
    </Modal>
  );
}

function StatusBadge({ status }: { status: PlannedOperationStatus }) {
  const { label, color } = STATUS[status];
  return (
    <Badge color={color} size="sm">
      {label}
    </Badge>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack gap={4}>
      <Text size="xs" tt="uppercase" fw={600} c="dimmed" style={{ letterSpacing: '0.06em' }}>
        {label}
      </Text>
      {typeof children === 'string' ? <Text size="sm">{children}</Text> : children}
    </Stack>
  );
}

function IdField({ label, value }: { label: string; value: string }) {
  return (
    <Field label={label}>
      <Group gap="xs" wrap="nowrap">
        <Text size="sm" ff="monospace" style={{ wordBreak: 'break-all' }}>
          {value}
        </Text>
        <CopyIdButton value={value} />
      </Group>
    </Field>
  );
}

function CopyIdButton({ value }: { value: string }) {
  return (
    <CopyButton value={value} timeout={1500}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? 'Скопировано' : 'Скопировать id'} withArrow>
          <ActionIcon
            variant="subtle"
            color={copied ? 'teal' : 'gray'}
            aria-label="Скопировать идентификатор"
            onClick={copy}
          >
            {copied ? <IconCheck size={16} stroke={1.5} /> : <IconCopy size={16} stroke={1.5} />}
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  );
}
